import { useForm, UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import useProductQuery from "../../hooks/useProductQuery";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { MembershipPrice, Product, ProductFormData, ProductListFilter, RetailPrice, SortKey } from "../../types/product";
import { useEffect, useState } from "react";
import localeCore from "../../core/locale.core";
import { Tag } from "../reuseables/list.comp";

import { convertProductToForm, updateProductPrice } from "../../core/product.core";
import { RetailShopRes } from "../../types/retailShop";
import useRetailList from "../../hooks/useRetailList.hook";
import useMemberships from "../../hooks/useMemberships.hook";
import { Membership } from "../../types/membership";
import { ChevronSmallDownIcon, ChevronSmallUpIcon, ChevronUpDownIcon } from "../../icons/chevronUpDown.icon";
import { toast } from "react-toastify";
import CheckIcon from "../../icons/check.icon";
import LoadingOvalIcon from "../../icons/loadingOval.icon";
import { useQueryClient } from "@tanstack/react-query";
import { inputCss } from "../reuseables/native.comp";

export default function ProductListPricesComp({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { t, msg } = useTranslation();
  const { data: retailShops, isFetched: retailsFetched } = useRetailList();
  const { data: memberships, isFetched: membershipsFetched } = useMemberships();
  const { data: products, isFetched, isError, failureReason } = useProductQuery(methods);

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched || !retailsFetched || !membershipsFetched) return <LoadingOvalCenterIcon />;

  return (
    <div id="product-section" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-5 gap-6 px-6 py-3 text-sm font-medium">
        {/* <div>{t(msg.product.details)}</div> */}
        <Title title={t(msg.product.price2)} name="price" methods={methods} />
        <div>{t(msg.product.eshopPrice)}</div>
        {retailShops && retailShops.length > 0 ? <div>{t(msg.product.retailPrices)}</div> : null}
        {memberships && memberships.length > 0 ? <div>{t(msg.product.membershipPrices)}</div> : null}
      </div>

      {products?.list.map((p) => (
        <ProductPricesRow product={p} key={p.id} retailShops={retailShops} memberships={memberships} />
      ))}
    </div>
  );
}

export function ProductPricesRow({
  product,
  retailShops,
  memberships,
}: {
  product: Product;
  retailShops?: RetailShopRes[];
  memberships?: Membership[];
}) {
  if (product.variations.length > 0) {
    return (
      <div className="border-t-[1px]">
        {product.variations.map((p) => (
          <PriceRowInput key={p.id} product={p} retailShops={retailShops} memberships={memberships} />
        ))}
      </div>
    );
  }
  return (
    <div className="border-t-[1px]">
      <PriceRowInput product={product} retailShops={retailShops} memberships={memberships} />;
    </div>
  );
}

function PriceRowInput({
  product,
  retailShops,
  memberships,
}: {
  product: Product;
  retailShops?: RetailShopRes[];
  memberships?: Membership[];
}) {
  const { t, msg } = useTranslation();

  const methods = useForm<ProductFormData>();
  const queryClient = useQueryClient();

  const handleSubmit = async (fieldName: string, price: number) => {
    const res = await updateProductPrice({ productId: product.id, [fieldName]: price });

    if (res.status !== 200) {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  useEffect(() => {
    if (product) methods.reset(convertProductToForm(product), { keepDirty: true });
  }, [product, methods]);

  // ensure form is populated before rendering row
  if (Object.keys(methods.getValues()).length < 1) return null;

  return (
    <div
      attr-name={localeCore.getLocaleValueDefault(product.name)}
      attr-sku={product.sku}
      attr-type="product-row"
      className={`grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-3 px-6 py-6  text-sm dark:text-white group/edit`}
    >
      {/* <div className="col-span-2 lg:col-span-1">
        <LocalePs value={product.name} />
      </div> */}
      <div>
        <Tag>{t(msg.product.price)}</Tag>
        <PriceInput
          fieldName="price"
          methods={methods}
          currentValue={product.price}
          handleSubmit={(price) => handleSubmit("price", price)}
        />
      </div>
      <div>
        <Tag>{t(msg.product.eshopPrice)}</Tag>
        <PriceInput
          fieldName="eshopPrice"
          methods={methods}
          currentValue={product.eshopPrice}
          handleSubmit={(price) => handleSubmit("eshopPrice", price)}
        />
      </div>

      {retailShops && retailShops.length > 0 ? (
        <div>
          <Tag>{t(msg.product.retailPrices)}</Tag>
          <div className="space-y-2">
            <PriceInput
              fieldName="defaultRetailPrice"
              methods={methods}
              placeholder={t(msg.product.default)}
              currentValue={product.defaultRetailPrice}
              handleSubmit={(price) => handleSubmit("defaultRetailPrice", price)}
            />
            <RetailListPrices
              product={product}
              methods={methods}
              retailShops={retailShops}
              currentRetailPrices={product.retailPrices}
            />
          </div>
        </div>
      ) : null}

      {memberships && memberships?.length > 0 ? (
        <div>
          <Tag>{t(msg.product.membershipPrices)}</Tag>
          <MembershipListPrices
            product={product}
            methods={methods}
            memberships={memberships}
            currentMembershipPrices={product.membershipPrices}
          />
        </div>
      ) : null}
    </div>
  );
}

export function PriceInput({
  methods,
  fieldName,
  placeholder,
  handleSubmit,
  currentValue,
}: {
  methods: UseFormReturn<ProductFormData>;
  fieldName: `membershipPrices.${number}.price` | `retailPrices.${number}.price` | "price" | "eshopPrice" | "discountPrice" | "defaultRetailPrice";
  placeholder?: string;
  handleSubmit: (price: number) => Promise<boolean>;
  currentValue: number;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prevValue, setPrevValue] = useState(currentValue);
  const value = useWatch({ name: fieldName, control: methods.control });

  const onSubmit = async () => {
    setIsSubmitting(true);
    const res = await handleSubmit(value as any);
    if (res) setPrevValue(Number(value));
    setIsSubmitting(false);
  };

  return (
    <div>
      <input
        type="number"
        {...methods.register(fieldName, {
          onChange: (e) => {
            console.log(fieldName, e.target.value, currentValue);
          },
        })}
        className={`${inputCss} border px-3 py-1 rounded-md max-w-24 sm:max-w-32`}
        placeholder={placeholder}
      />

      <div className="inline-block ml-2">
        {!isSubmitting && !!value && prevValue !== Number(value) ? (
          <button className="button-yes cursor-pointer" onClick={onSubmit}>
            <CheckIcon className="h-4 w-4 text-green-600 hover:h-5 hover:w-5" />
          </button>
        ) : null}

        {isSubmitting ? <LoadingOvalIcon className="w-5 h-5" /> : null}
      </div>
    </div>
  );
}

export interface RetailListPricesProps {
  product: Product;
  retailShops?: RetailShopRes[];
  methods: UseFormReturn<ProductFormData, any>;
  currentRetailPrices: RetailPrice[] | undefined;
}

export const RetailListPrices = ({ product, retailShops, methods, currentRetailPrices }: RetailListPricesProps) => {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const retailPrices = useWatch({
    name: "retailPrices",
    control: methods.control,
  });

  useEffect(() => {
    const prices = retailShops?.map((s) => {
      const retailShop = retailPrices?.find((p) => p.retailShopId === s.id);
      return {
        retailShopId: s.id, //
        price: retailShop?.price ?? null,
      };
    });

    methods.setValue("retailPrices", prices as never);
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (retailShopId: string, price: number) => {
    const res = await updateProductPrice({
      productId: product.id,
      retailPrice: { retailShopId, price },
    });

    if (res.status !== 200) {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  if (!retailShops) return null;

  return (
    <div className="space-y-2">
      {retailPrices?.map((retailPrice, index) => {
        const shopName = retailShops.find((s) => s.id === retailPrice.retailShopId)?.name;
        const currentPrice = currentRetailPrices?.find((p) => p.retailShopId === retailPrice.retailShopId);
        return (
          <PriceInput
            key={`${retailPrice.retailShopId}-${index}`}
            methods={methods}
            fieldName={`retailPrices.${index}.price`}
            placeholder={shopName}
            handleSubmit={(value) => handleSubmit(retailPrice.retailShopId, value)}
            currentValue={Number(currentPrice?.price)}
          />
        );
      })}
    </div>
  );
};

export interface MembershipListPricesProps {
  product: Product;
  memberships?: Membership[];
  methods: UseFormReturn<ProductFormData, any>;
  currentMembershipPrices?: MembershipPrice[];
}
export const MembershipListPrices = ({ product, memberships, methods, currentMembershipPrices }: MembershipListPricesProps) => {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const membershipPrices = useWatch({
    name: "membershipPrices",
    control: methods.control,
  });

  useEffect(() => {
    const prices = memberships?.map((s) => {
      const membership = membershipPrices?.find((p) => p.membershipId === s.id);
      return {
        membershipId: s.id, //
        price: membership?.price ?? null,
      };
    });

    methods.setValue("membershipPrices", prices as never);
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (membershipId: string, price: number) => {
    const res = await updateProductPrice({
      productId: product.id,
      membershipPrice: { membershipId, price },
    });

    if (res.status !== 200) {
      toast.error(t(msg.errors.contactAdmin));
      return false;
    }

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }
    return true;
  };

  if (!memberships) return null;

  return (
    <div className="space-y-2">
      {membershipPrices?.map((membershipPrice, index) => {
        const localeName = memberships.find((s) => s.id === membershipPrice.membershipId)?.name;
        const membershipName = localeCore.getLocaleValueDefault(localeName) ?? "";
        const currentPrice = currentMembershipPrices?.find((p) => p.membershipId === membershipPrice.membershipId);

        return (
          <PriceInput
            key={`${membershipPrice.membershipId}-${index}`}
            methods={methods}
            fieldName={`membershipPrices.${index}.price`}
            placeholder={membershipName}
            handleSubmit={(value) => handleSubmit(membershipPrice.membershipId, value)}
            currentValue={Number(currentPrice?.price)}
          />
        );
      })}
    </div>
  );
};

export function Title({
  methods,
  title,
  name,
}: {
  methods: UseFormReturn<ProductListFilter, any>;
  title: string;
  name: SortKey;
}) {
  const [sort, isSortAsec] = useWatch({
    name: ["sort", "sortAsec"],
    control: methods.control,
  });

  function click() {
    methods.setValue("sort", name);
    methods.setValue("sortAsec", sort !== name ? 1 : isSortAsec === 1 ? -1 : 1);
  }

  return (
    <button id={`sort_${name}`} className="flex items-center gap-2" onClick={click}>
      {title}
      {sort !== name && <ChevronUpDownIcon />}
      {sort === name && isSortAsec === 1 && <ChevronSmallUpIcon />}
      {sort === name && isSortAsec === -1 && <ChevronSmallDownIcon />}
    </button>
  );
}
